/**
 * GeometryService - Non-reactive geometry handling service
 *
 * This service provides utilities for working with geometry data without
 * Vue's reactivity system interfering with Leaflet operations.
 */
import L from "leaflet";

class GeometryService {
  /**
   * Creates a deep copy of geometry data without Vue reactivity
   * @param {Object} geometry - The geometry object to copy
   * @returns {Object} Non-reactive copy of the geometry
   */
  static createNonReactiveClone(geometry) {
    if (!geometry) {
      return null;
    }

    // Use structured cloning for a clean copy without reactivity
    return JSON.parse(JSON.stringify(geometry));
  }

  /**
   * Converts Leaflet layer to clean GeoJSON geometry without reactivity
   * @param {L.Layer} layer - The Leaflet layer
   * @returns {Object} Clean GeoJSON geometry object
   */
  static layerToCleanGeometry(layer) {
    if (!layer || typeof layer.toGeoJSON !== "function") {
      return null;
    }

    try {
      const geoJson = layer.toGeoJSON();
      return this.createNonReactiveClone(geoJson.geometry);
    } catch (error) {
      console.error("Error converting layer to geometry:", error);
      return null;
    }
  }

  /**
   * Creates a Leaflet layer from geometry data, ensuring no reactivity contamination
   * @param {Object} geometry - GeoJSON geometry object
   * @param {Object} options - Leaflet layer options
   * @returns {L.Layer} Leaflet layer
   */
  static geometryToLayer(geometry, options = {}) {
    if (!geometry) {
      return null;
    }

    try {
      // Create a clean, non-reactive copy of the geometry
      const cleanGeometry = this.createNonReactiveClone(geometry);

      // Create GeoJSON feature
      const feature = {
        type: "Feature",
        geometry: cleanGeometry,
        properties: {},
      };

      return L.geoJSON(feature, options);
    } catch (error) {
      console.error("Error creating layer from geometry:", error);
      return null;
    }
  }

  /**
   * Validates geometry structure
   * @param {Object} geometry - GeoJSON geometry object
   * @returns {boolean} True if geometry is valid
   */
  static isValidGeometry(geometry) {
    if (!geometry || typeof geometry !== "object") {
      return false;
    }

    const validTypes = [
      "Point",
      "LineString",
      "Polygon",
      "MultiPoint",
      "MultiLineString",
      "MultiPolygon",
    ];

    return (
      validTypes.includes(geometry.type) &&
      Array.isArray(geometry.coordinates) &&
      geometry.coordinates.length > 0
    );
  }

  /**
   * Extracts coordinates from Leaflet layer without reactivity
   * @param {L.Layer} layer - The Leaflet layer
   * @returns {Array} Array of coordinates
   */
  static extractCleanCoordinates(layer) {
    if (!layer || typeof layer.getLatLngs !== "function") {
      return null;
    }

    try {
      const latlngs = layer.getLatLngs();

      // Remove Vue reactivity by creating clean coordinate arrays
      return this.cleanLatLngsArray(latlngs);
    } catch (error) {
      console.error("Error extracting coordinates:", error);
      return null;
    }
  }

  /**
   * Recursively cleans LatLng arrays of Vue reactivity
   * @param {Array} latlngs - Array of LatLng objects or nested arrays
   * @returns {Array} Clean coordinate arrays
   */
  static cleanLatLngsArray(latlngs) {
    if (!Array.isArray(latlngs)) {
      return null;
    }

    return latlngs
      .map((item) => {
        if (Array.isArray(item)) {
          // Nested array (polygon with holes or multipolygon)
          return this.cleanLatLngsArray(item);
        } else if (
          item &&
          typeof item.lat === "number" &&
          typeof item.lng === "number"
        ) {
          // LatLng object - return clean coordinate pair
          return [item.lng, item.lat]; // GeoJSON format: [longitude, latitude]
        } else {
          console.warn("Unexpected coordinate format:", item);
          return null;
        }
      })
      .filter((item) => item !== null);
  }

  /**
   * Converts GeoJSON coordinates to Leaflet LatLng format
   * @param {Array} coordinates - GeoJSON coordinates
   * @returns {Array} Array of L.LatLng objects
   */
  static coordinatesToLatLngs(coordinates) {
    if (!Array.isArray(coordinates)) {
      return [];
    }

    return coordinates
      .map((coord) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          if (Array.isArray(coord[0])) {
            // Nested array - recursively process
            return this.coordinatesToLatLngs(coord);
          } else {
            // Coordinate pair [lng, lat]
            return L.latLng(coord[1], coord[0]);
          }
        }
        return null;
      })
      .filter((item) => item !== null);
  }

  /**
   * Calculates bounds from geometry without using reactive data
   * @param {Object} geometry - GeoJSON geometry object
   * @returns {L.LatLngBounds} Leaflet bounds object
   */
  static calculateBounds(geometry) {
    if (!this.isValidGeometry(geometry)) {
      return null;
    }

    try {
      const cleanGeometry = this.createNonReactiveClone(geometry);
      const tempLayer = L.geoJSON({
        type: "Feature",
        geometry: cleanGeometry,
        properties: {},
      });

      return tempLayer.getBounds();
    } catch (error) {
      console.error("Error calculating bounds:", error);
      return null;
    }
  }

  /**
   * Merges multiple geometries into a single geometry collection
   * @param {Array} geometries - Array of GeoJSON geometry objects
   * @returns {Object} GeometryCollection or single geometry
   */
  static mergeGeometries(geometries) {
    if (!Array.isArray(geometries) || geometries.length === 0) {
      return null;
    }

    const validGeometries = geometries
      .map((g) => this.createNonReactiveClone(g))
      .filter((g) => this.isValidGeometry(g));

    if (validGeometries.length === 0) {
      return null;
    }

    if (validGeometries.length === 1) {
      return validGeometries[0];
    }

    return {
      type: "GeometryCollection",
      geometries: validGeometries,
    };
  }

  /**
   * Updates polygon vertex coordinates without reactivity issues
   * @param {Object} geometry - Original polygon geometry
   * @param {number} vertexIndex - Index of vertex to update
   * @param {L.LatLng} newLatLng - New coordinates
   * @returns {Object} Updated geometry
   */
  static updatePolygonVertex(geometry, vertexIndex, newLatLng) {
    if (!this.isValidGeometry(geometry) || geometry.type !== "Polygon") {
      console.error("Invalid polygon geometry for vertex update");
      return geometry;
    }

    try {
      const cleanGeometry = this.createNonReactiveClone(geometry);
      const coordinates = cleanGeometry.coordinates;

      if (
        Array.isArray(coordinates[0]) &&
        vertexIndex < coordinates[0].length
      ) {
        // Update the vertex coordinate [lng, lat]
        coordinates[0][vertexIndex] = [newLatLng.lng, newLatLng.lat];
      }

      return cleanGeometry;
    } catch (error) {
      console.error("Error updating polygon vertex:", error);
      return geometry;
    }
  }

  /**
   * Creates a frozen (non-reactive) version of geometry data
   * @param {Object} geometry - Geometry object to freeze
   * @returns {Object} Frozen geometry object
   */
  static freezeGeometry(geometry) {
    if (!geometry) {
      return null;
    }

    const cleanGeometry = this.createNonReactiveClone(geometry);
    return Object.freeze(cleanGeometry);
  }

  /**
   * Checks if an object has Vue reactivity
   * @param {*} obj - Object to check
   * @returns {boolean} True if object has Vue observer
   */
  static hasVueReactivity(obj) {
    return obj && typeof obj === "object" && obj.__ob__;
  }

  /**
   * Removes Vue reactivity from nested objects
   * @param {*} obj - Object to clean
   * @returns {*} Clean object without reactivity
   */
  static removeReactivity(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeReactivity(item));
    }

    if (typeof obj === "object") {
      if (this.hasVueReactivity(obj)) {
        // Use JSON serialization for deep reactive objects
        return JSON.parse(JSON.stringify(obj));
      }

      // For non-reactive objects, still clean nested properties
      const cleaned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cleaned[key] = this.removeReactivity(obj[key]);
        }
      }
      return cleaned;
    }

    return obj;
  }

  // ============ Drag-specific utilities for leaflet-path-transform ============

  /**
   * Creates a draggable layer from geometry with transform capabilities
   * @param {Object} geometry - GeoJSON geometry object
   * @param {Object} options - Layer styling and behavior options
   * @returns {L.Layer} Leaflet layer with transform capabilities
   */
  static createDraggableLayer(geometry, options = {}) {
    if (!this.isValidGeometry(geometry)) {
      return null;
    }

    try {
      const cleanGeometry = this.createNonReactiveClone(geometry);

      // Default drag styling
      const defaultOptions = {
        fillColor: "#ff9900",
        fillOpacity: 0.4,
        color: "#ff6600",
        weight: 3,
        dashArray: "5,5",
      };

      const mergedOptions = { ...defaultOptions, ...options };

      // Create layer from clean geometry
      if (geometry.type === "Polygon") {
        const coordinates = cleanGeometry.coordinates[0];
        const latLngs = coordinates.map((coord) => [coord[1], coord[0]]);
        return L.polygon(latLngs, mergedOptions);
      }

      // Fallback to GeoJSON for other geometry types
      return this.geometryToLayer(cleanGeometry, mergedOptions);
    } catch (error) {
      console.error("Error creating draggable layer:", error);
      return null;
    }
  }

  /**
   * Applies drag transformation offset to geometry
   * @param {Object} geometry - Original GeoJSON geometry
   * @param {Object} offset - Transformation offset {lat, lng}
   * @returns {Object} Transformed geometry
   */
  static applyDragTransform(geometry, offset) {
    if (!this.isValidGeometry(geometry) || !offset) {
      return geometry;
    }

    try {
      const cleanGeometry = this.createNonReactiveClone(geometry);

      if (geometry.type === "Polygon") {
        cleanGeometry.coordinates = cleanGeometry.coordinates.map((ring) =>
          ring.map((coord) => [coord[0] + offset.lng, coord[1] + offset.lat]),
        );
      } else if (geometry.type === "MultiPolygon") {
        cleanGeometry.coordinates = cleanGeometry.coordinates.map((polygon) =>
          polygon.map((ring) =>
            ring.map((coord) => [coord[0] + offset.lng, coord[1] + offset.lat]),
          ),
        );
      }

      return cleanGeometry;
    } catch (error) {
      console.error("Error applying drag transform:", error);
      return geometry;
    }
  }

  /**
   * Validates if drag operation keeps geometry within bounds
   * @param {Object} geometry - GeoJSON geometry to validate
   * @param {L.LatLngBounds} bounds - Allowed bounds for the geometry
   * @returns {boolean} True if geometry is within bounds
   */
  static validateDragBounds(geometry, bounds) {
    if (!this.isValidGeometry(geometry) || !bounds) {
      return false;
    }

    try {
      const geometryBounds = this.calculateBounds(geometry);
      return bounds.contains(geometryBounds);
    } catch (error) {
      console.error("Error validating drag bounds:", error);
      return false;
    }
  }

  /**
   * Calculates the center point of a geometry for drag operations
   * @param {Object} geometry - GeoJSON geometry
   * @returns {L.LatLng} Center point
   */
  static getGeometryCenter(geometry) {
    if (!this.isValidGeometry(geometry)) {
      return null;
    }

    try {
      const bounds = this.calculateBounds(geometry);
      return bounds ? bounds.getCenter() : null;
    } catch (error) {
      console.error("Error getting geometry center:", error);
      return null;
    }
  }

  /**
   * Removes one polygon from a MultiPolygon. If exactly one polygon remains,
   * collapses the result to a Polygon. Returns the original geometry unchanged
   * if it is not a MultiPolygon or the index is out of range.
   * @param {Object} geometry - GeoJSON MultiPolygon
   * @param {number} partIndex - Index of polygon to remove
   * @returns {Object} New GeoJSON geometry (Polygon or MultiPolygon)
   */
  static removePolygonFromMultiPolygon(geometry, partIndex) {
    if (!geometry || geometry.type !== "MultiPolygon") {
      return geometry;
    }
    const clone = this.createNonReactiveClone(geometry);
    if (partIndex < 0 || partIndex >= clone.coordinates.length) {
      return clone;
    }
    clone.coordinates.splice(partIndex, 1);
    if (clone.coordinates.length === 1) {
      return { type: "Polygon", coordinates: clone.coordinates[0] };
    }
    return clone;
  }

  /**
   * Optimizes geometry for drag performance by simplifying if needed
   * @param {Object} geometry - GeoJSON geometry
   * @param {number} tolerance - Simplification tolerance (optional)
   * @returns {Object} Optimized geometry
   */
  static optimizeForDrag(geometry, tolerance = 0.0001) {
    if (!this.isValidGeometry(geometry)) {
      return geometry;
    }

    try {
      const cleanGeometry = this.createNonReactiveClone(geometry);

      // For polygons with many vertices, consider simplification
      if (geometry.type === "Polygon" && geometry.coordinates[0].length > 100) {
        console.log(
          `Optimizing polygon with ${geometry.coordinates[0].length} vertices for drag`,
        );
        // Note: Actual simplification would require a library like turf.js
        // For now, just return clean geometry
      }

      return cleanGeometry;
    } catch (error) {
      console.error("Error optimizing geometry for drag:", error);
      return geometry;
    }
  }
}

export default GeometryService;

